import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import {
  BookingStatus,
  IProductSlotState,
  IQuantifiedProductSlotState,
  PaymentStatus,
  PSSType,
} from "@/types/db/product-slot-state";
import { groupedPSS } from "../vue-helpers/booking";
import {
  IOfferApplication,
  IOrderContent,
  IOrderItem,
  OffersCommon,
} from "@smeetz/offers-common";
import { generateUuidv4, isEmptyObj } from "./app";
import { SharedCallbacks } from "../shared-resources";
import OffersModule from "@/store/modules/offers";
import { getProductCategoryById } from "@/dexie/product-category";
import BookingModule from "@/store/modules/booking";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  createOffersDiscountAccounting,
  isPSSPaid,
  totalDiscountsByPssWithOffersConfirmed,
} from "../vue-helpers/new-accountings";
import {
  AccountingState,
  IAccounting,
  PaymentMethods,
  PromocodeCampaignType,
  TransactionTypes,
} from "@/types/db/accounting";
import { createCancelPSSDiscountAccounting } from "../vue-helpers/accounting";
import { sumArray } from "./common";
import { LogService } from "@/services/logging";
import { hasSalesStarted } from "../vue-helpers/cash-float";
import { cloneDeep } from "lodash";
import { IAccountingEntry } from "@/types/db/accounting-entry";

export async function loadOffers() {
  const env = import.meta.env.VITE_ENV || "test";
  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
  const apiKey = String(import.meta.env.VITE_X_API_KEY);
  if (hasSalesStarted.value && env && groupId) {
    // instantiate offers class if sales are open
    const offersCommon = await OffersCommon.Create(env, groupId, apiKey);
    LogService.info("Offers library object instantiated");
    OffersModule.setOffersCommon({ offersCommon });
  }
}
/**
 * gets available offers, called after each update to the list
 */
export async function getOfferApplication(booking: IProductSlotBooking) {
  console.log("--------- hi from getOfferApplication");
  const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
  const userId = UserModule.userId;
  const bookingUuid = booking.uuid;
  const pssList = booking.pssList;
  if (
    !groupId ||
    !userId ||
    !OffersModule.offersCommon ||
    !booking ||
    !bookingUuid
  ) {
    return;
  }
  const { role, roleName } = UserModule.user.userGroups.find(
    (ug) => ug.group.id === UserModule.getUserGroupId
  ) || { role: undefined, roleName: undefined };

  const groupedPssList: IQuantifiedProductSlotState[] = groupedPSS(pssList, {
    byDiscountsExcludingOffers: true,
  });

  // construct an orderItem object for each grouped PSS and insert it to ordderItems which will be passed to orderContent
  const orderItems: IOrderItem[] = [];
  for (let i = 0; i < groupedPssList.length; i++) {
    const pssGroup = groupedPssList[i];
    // get one pss from the pss group to get missing fields.
    const pssSample = pssList.find((pss) => pss.uuid === pssGroup.uuid);
    if (!pssSample) return;
    const { paymentStatus, retailOptionName, categoryId } = pssSample;
    const productCategory = await getProductCategoryById(categoryId);
    if (!productCategory) {
      continue;
    }
    const price = productCategory.prices.find((p) => p.id === pssGroup.priceId);
    if (!price || !price.priceCategory) {
      continue;
    }
    const salesCollectionIds = productCategory.ticketGroups;
    const confirmedAppliedOffers: {
      offerId: number;
      offerApplicationUuid: string;
    }[] = [];
    const confirmedOffersAccountings = booking.accountings.filter(
      (acc) =>
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
        acc.state === AccountingState.Completed &&
        !acc.refund &&
        (acc.paymentMethod === PaymentMethods.METHOD_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER) &&
        acc.accountingEntriesList.some((ae) =>
          pssGroup.pssIds.includes(String(ae.productSlotStateUuid))
        )
    );

    const orderItem: IOrderItem = {
      quantity: pssGroup.quantity,
      type:
        pssGroup.pssType === PSSType[PSSType.Ticket]
          ? PSSType.Ticket
          : PSSType.Retail,
      bookingStatus: pssGroup.bookingStatus as number,
      paymentStatus: paymentStatus,
      priceId: pssGroup.priceId,
      timeslotId: pssGroup.timeSlotId || undefined,
      retailOptionId: pssGroup.retailOptionId,
      retailOptionName: retailOptionName,
      salesCollectionIds,
      priceCategory: price.priceCategory,
      originalPriceValue: pssGroup.priceValue / 100,
      confirmedDiscounts:
        totalDiscountsByPssWithOffersConfirmed.value(pssGroup.pssIds[0]) / 100,
      startDateTime: pssGroup.startDateTime,
      endDateTime: pssGroup.endDateTime,
      categoryId: pssGroup.categoryId,
      productId: productCategory.product.id,
    };
    if (confirmedOffersAccountings.length) {
      orderItem.appliedOffers = confirmedOffersAccountings.map((acc) => {
        return {
          offerId: Number(acc.accountingEntriesList[0].offerId),
          offerApplicationUuid: acc.uuid as string,
        };
      });
    }
    orderItems.push(orderItem);
  }

  console.log("------------ done with orderItems");

  // Newly applied code from Promocode input
  console.log("------------ BookingModule.appliedPromocodes");
  console.log(JSON.parse(JSON.stringify(BookingModule.appliedPromocodes)));

  const newlyAppliedCodes = BookingModule.appliedPromocodes
    .filter(
      (promocode) =>
        promocode.bookingUuid === bookingUuid &&
        promocode.offerIds.length > 0 &&
        // memberships are handled separately
        !promocode.membershipId
    )
    .map((offerCode) => {
      return {
        offerId: offerCode.offerIds[0],
        code: offerCode.promocode.code,
        codeId: offerCode.promocode.promoCodeId,
      };
    });

  console.log("------------ newlyAppliedCodes");
  console.log(newlyAppliedCodes);

  // Already applied offers with codes
  let offerCodes: any[] = [];
  if (!isEmptyObj(OffersModule.appliedOffers)) {
    console.log("------------ object is not empty");
    console.log("------------ querying offerModule.");
    console.log(JSON.parse(JSON.stringify(OffersModule.appliedOffers)));
    console.log("------------ offer codes applied on the current booking");
    console.log(
      OffersModule.appliedOffers[BookingModule.selectedBooking?.uuid as string]
    );
    // Lets check first if the current booking has offers codes
    if (
      OffersModule.appliedOffers[BookingModule.selectedBooking?.uuid as string]
    ) {
      offerCodes = OffersModule.appliedOffers[
        BookingModule.selectedBooking?.uuid as string
      ]
        .filter(
          (offerApp) =>
            offerApp.isOfferStillEligible && offerApp.code && offerApp.codeId
        )
        .map((offerApp) => {
          return {
            code: offerApp.code as string,
            codeId: offerApp.codeId as number,
            offerId: offerApp.offerId,
          };
        });

      console.log("------- DONE querying offerModule.");

      console.log("--------------- offerCodes");
      console.log(offerCodes);
    }
  }

  // appliedCodes will be a concatenatio of newlyAppliedCodes + already applied codes from OffersModule
  const appliedCodes = [...newlyAppliedCodes, ...offerCodes].map(
    (offerCode) => {
      return {
        offerId: offerCode.offerId,
        code: offerCode.code,
        codeId: offerCode.codeId,
      };
    }
  );
  console.log("--------------- appliedCodes");
  console.log(appliedCodes);

  const offerMembership = BookingModule.appliedPromocodes.find(
    (promocode) =>
      promocode.bookingUuid === bookingUuid &&
      promocode.offerIds.length > 0 &&
      !!promocode.membershipId
  );

  const scannedMembership = offerMembership
    ? {
        membershipId: offerMembership.membershipId as number, // safe because offerMembership needs to have a defined membershipId
        codeId: offerMembership.promocode.promoCodeId,
        code: offerMembership.promocode.code,
      }
    : undefined;

  let stillEligibleOfferApplications: IOfferApplication[] = [];
  stillEligibleOfferApplications = getNotFullyRefundedOfferApplications(
    OffersModule.appliedOffers[bookingUuid]
  );

  console.log("------------ going to create orderContent");

  const orderContent: IOrderContent = {
    uid: generateUuidv4(),
    groupId,
    sellerUserId: userId,
    sellerRoleId: role,
    sellerRoleName: roleName,
    posConfigId: SharedCallbacks.getPOSConfigId
      ? SharedCallbacks.getPOSConfigId()
      : undefined,

    orderCreationDateTime: booking.orderSessions.find(
      (os) => os.id === BookingModule.currentOrderSessionId
    )?.createdAt,

    appliedCodes,

    scannedMembership,

    channel: "Smeetz POS",
    orderItems,
    offerApplications: stillEligibleOfferApplications,
  };

  LogService.info(
    "Searching for applicable offers with following order content",
    { orderContent }
  );
  console.log("---------------- calling applyOffers() with orderContent");
  console.log(cloneDeep(orderContent));
  const response = OffersModule.offersCommon.applyOffers(orderContent);
  LogService.info("Offers response attached", { response });
  return response;
}

/**
 *
 * Not Fully refunded offer applications are those that are still eligible for the booking and there is no refund accountings with equal value
 *
 */
function getNotFullyRefundedOfferApplications(
  offerApplicationsFromModule: IOfferApplication[]
): IOfferApplication[] {
  console.log(
    "-------------------- hi from getNotFullyRefundedOfferApplications"
  );
  if (!offerApplicationsFromModule || !offerApplicationsFromModule.length) {
    return [];
  }

  const notFullyRefundedOfferApplications: IOfferApplication[] = [];

  for (const offerApplication of offerApplicationsFromModule) {
    const { applicationUuid, isOfferStillEligible } = offerApplication;

    if (!isOfferStillEligible) {
      continue;
    }

    // check if there is a refund accounting for the offer application
    const offerRefundAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.originalAccountingUuid === applicationUuid &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
          acc.state === AccountingState.Completed &&
          acc.refund
      );
    console.log("offerRefundAccountings");
    console.log(cloneDeep(offerRefundAccountings));

    if (!offerRefundAccountings || !offerRefundAccountings.length) {
      notFullyRefundedOfferApplications.push(offerApplication);
    } else {
      // check if the refund accounting has the same value as the offer application
      const totalRefundValue = offerRefundAccountings.reduce(
        (sum, acc) =>
          sum +
          acc.accountingEntriesList.reduce(
            (accSum, ae) => accSum + ae.value,
            0
          ),
        0
      );

      const totalOfferValue = offerApplication.applicationDetails.reduce(
        (sum, detail) =>
          sum + (detail.discountValuePerItem || 0) * detail.discountItemsCount,
        0
      );

      if (totalRefundValue < totalOfferValue) {
        notFullyRefundedOfferApplications.push(offerApplication);
      }
    }
  }
  console.log("----------------- notFullyRefundedOfferApplications");
  console.log(cloneDeep(notFullyRefundedOfferApplications));

  return notFullyRefundedOfferApplications;
}

/**
 *
 * Finds and applies offers to a given booking
 */
export async function applyOffersToBooking(booking: IProductSlotBooking) {
  console.log("-------------- hi from applyOffersToBooking --------------");
  console.log("booking");
  console.log(cloneDeep(booking));
  let orderContentWithOfferApplications: IOrderContent | null | undefined;
  try {
    orderContentWithOfferApplications = await getOfferApplication(booking);
    console.log("-------------- orderContentWithOfferApplications");
    console.log(JSON.parse(JSON.stringify(orderContentWithOfferApplications)));
    LogService.info(
      "Got response from offers library",
      orderContentWithOfferApplications
    );
  } catch (error) {
    LogService.info("An error occurred when getting offers", { error });
    return;
  }

  // start by removing old applied offers and replace them with new ones
  const accountingsAfterRemovingOffers = removeAppliedOffers(booking);
  booking.accountings = accountingsAfterRemovingOffers;

  OffersModule.setAppliedOffers({
    bookingUuid: booking.uuid as string,
    offerApplications:
      orderContentWithOfferApplications?.offerApplications || [],
  });

  if (!orderContentWithOfferApplications) {
    findAndRefundUneligibleOffers({
      booking,
      applicationUuid: undefined,
      allApplications: true,
    });
    return;
  }
  const { offerApplications } = orderContentWithOfferApplications;

  if (!offerApplications) {
    return;
  }

  const discountAccountings: IAccounting[] = [];
  for (let i = 0; i < offerApplications.length; i++) {
    const offerApplication = offerApplications[i];

    const {
      offerId,
      code,
      codeId,
      applicationDetails,
      isOfferStillEligible,
      applicationUuid,
    } = offerApplication;

    // !isOfferStillEligible, it means this offerApplication is not eligible anymore, lets refund its accounting
    if (!isOfferStillEligible) {
      // look for accountings with the same application uuid that is no longer eligible and refund them
      findAndRefundUneligibleOffers({ booking, applicationUuid });
      continue;
    }

    // // 2- The offerApplication is still eligible
    // // Lets check if it has cancelledTickets so that we refund their offer discount
    // // How come? User can cancel a discounted ticket by offerApplication without making the full offerApplication not eligible anymore
    // if (offerApplication.applicationDetails.some((apD) => apD.cancelledDiscountItemsCount)) {
    //   // So this offerApplication although it is still eligible, it is applied on cancelled tickets
    //   // and we need to refund those tickets
    //   const cancelledDiscountItemsDetails = offerApplication.applicationDetails.filter((apD) => apD.cancelledDiscountItemsCount).map((oAp)=>{
    //     return {cancelledDiscountItemsCount: Number(oAp.cancelledDiscountItemsCount), priceId: oAp.priceId, categoryId: oAp.categoryId, productId: oAp.productId, offerId: offerApplication.offerId}
    //   });

    //   if (cancelledDiscountItemsDetails && cancelledDiscountItemsDetails.length) {
    //     for (let i = 0; i < cancelledDiscountItemsDetails.length; i++) {
    //       findAndRefundCancelledTicketsOffersDiscounts({
    //         booking,
    //         applicationUuid,
    //         cancelledTickets: cancelledDiscountItemsDetails[i],
    //       });
    //     }
    //   }
    //   // We finished refunding cancelled tickets that have an offer applied on them for this offerApplication
    //   // Lets move to the next offerApplication
    //   continue;
    // }

    // 3- So the offerApplication is still eligible and has no cancelled tickets
    // Lets see if this offerApplication has already an accounting created so that we don't recreate it
    const doesAccountingExistAlready = booking.accountings.some(
      (acc) => acc.uuid === offerApplication.applicationUuid
    );
    if (doesAccountingExistAlready) {
      console.log(
        "--------- offer accounting already exists, moving to next one"
      );
      continue;
    }

    const pssToDiscount: {
      applicationDetailPss: IProductSlotState[];
      valueInCents: number;
    }[] = [];
    const dynamicPssList = JSON.parse(JSON.stringify(booking.pssList));

    for (let j = 0; j < applicationDetails.length; j++) {
      const applicationDetail = applicationDetails[j];
      const {
        priceId,
        categoryId,
        productId,
        ticketGroupId,
        discountItemsCount,
        discountValuePerItem,
      } = applicationDetail;
      if (!discountValuePerItem) {
        continue;
      }
      // TODO Hamza:
      // POS now is supplying a pss that is fully discounted by Offer accounting with state:1
      // this is why the condition (&& !isPSSPaid(pss)) is not enough
      // so we need to make sure the pss is not fully discounted by offer accounting with state:5 or with state:1
      const applicationDetailPss = dynamicPssList
        .filter(
          (pss: any) =>
            pss.priceId === priceId &&
            pss.bookingStatus !== BookingStatus.Canceled &&
            pss.bookingStatus !== BookingStatus.Abandoned &&
            !isPSSPaid(pss)
        ) /*slice is randomly taking the first X pss, it means we can fall in a case where we create the same offer accountingEntry for the same pss again and again*/
        .slice(0, discountItemsCount);
      console.log("applicationDetailPss");
      console.log(cloneDeep(applicationDetailPss));
      // Lets now cut same items that are we are going to push to applicationDetailPss from dynamicPssList so that we do not use them again in the next iteration of the loop so that we don't end up with applying the same offer more than once without the library saying so
      // note that current function applies one offer at a time, so if the library says that offer A can be applied twice, then it will be in two different function calls
      applicationDetailPss.forEach((pss: any) => {
        const index = dynamicPssList.findIndex((p: any) => p.uuid === pss.uuid);
        if (index > -1) {
          dynamicPssList.splice(index, 1);
        }
      });

      pssToDiscount.push({
        applicationDetailPss,
        valueInCents: discountValuePerItem * 100,
      });
    }

    if (!pssToDiscount.length) {
      continue;
    }
    console.log(
      "--------- creating discountAccounting for offerApplication: ",
      applicationUuid
    );
    const discountAccounting = createOffersDiscountAccounting({
      booking,
      applicationUuid,
      pssDiscountInfo: pssToDiscount,
      offerId,
      code,
      codeId,
    });
    discountAccountings.push(discountAccounting);
  }
  BookingModule.updateBookingAccountings({
    bookingUUID: booking.uuid as string,
    accountings: [...booking.accountings, ...discountAccountings],
  });
}

/**
 * removes unconfirmed offers accountings and cancels confirmed ones
 */
export function removeAppliedOffers(booking: IProductSlotBooking) {
  OffersModule.clearBookingOffers(booking.uuid as string);
  BookingModule.removeOfferPromocodes(booking.uuid as string);
  const newAccountings: IAccounting[] = [];

  for (let i = 0; i < booking.accountings.length; i++) {
    const accounting = booking.accountings[i];

    if (
      accounting.paymentMethod === PaymentMethods.METHOD_POS_OFFER &&
      !accounting.id &&
      accounting.state === AccountingState.Pending
    ) {
      // ignore all newly created offers accountings
      continue;
    }

    newAccountings.push(accounting);
  }

  BookingModule.updateBookingAccountings({
    bookingUUID: booking.uuid as string,
    accountings: newAccountings,
  });

  return newAccountings;
}

/**
 *
 * 1- Look for accountings with the same application uuid that is no longer eligible and refund them
 *
 */
function findAndRefundUneligibleOffers(data: {
  booking: IProductSlotBooking;
  applicationUuid: string | undefined;
  allApplications?: boolean; // if set to true, all offer accountings will be refunded
}) {
  console.log(
    "----------------- hi from findAndRefundUneligibleOffers",
    cloneDeep(data)
  );
  const { booking, applicationUuid, allApplications } = data;
  let offerAccountings: IAccounting[] = [];
  if (allApplications) {
    // get offer accountings that have not been refunded already
    offerAccountings = booking.accountings.filter(
      (acc) =>
        (acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_OFFER) &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
        acc.state === 5 &&
        !acc.refund
    );
  } else if (applicationUuid) {
    offerAccountings = booking.accountings.filter(
      (acc) => acc.uuid === applicationUuid
    );
  } else {
    return;
  }

  offerAccountings.forEach((offerAcc) => {
    // if some pss already have refund accountings, we do not create them again
    const pssWithoutOfferRefunds = getPssWithoutOfferRefunds(booking, offerAcc);
    const linkedPss = booking.pssList.filter((pss) =>
      pssWithoutOfferRefunds.includes(pss.uuid)
    );
    if (!linkedPss.length) {
      return;
    }
    console.log(
      "----------------- going to call createCancelPSSDiscountAccounting 1"
    );
    const refundAccounting = createCancelPSSDiscountAccounting(
      booking,
      linkedPss,
      offerAcc
    );
    if (refundAccounting) {
      booking.accountings.push(refundAccounting);
    }
  });

  BookingModule.updateBookingAccountings({
    bookingUUID: booking.uuid as string,
    accountings: booking.accountings,
  });
}

/**
 * Refund an exact offer on exact tickets after they get cancelled without necessarily having the offerApplication not eligible anymore
 * When does this happen?
 * 1- An offer required X tickets as a condition and was applied on X tickets
 * 2- Order has Y tickets while Y = X + 3
 * 3- User cancels 1 ticket that had the offer --> Offer is still eligible but one ticket should have its offer discount refunded
 * 4- If user keeps cancelling tickets until he cancells 4 ticket, the library will tell you that the offer is not eligible and POS will automatically refund it.
 * 5- Keep in mind that actions (3) and (4) can happen in two different days, so POS when refunding the full application in action(4) it should not refund tickets that are already refunded
 */
function findAndRefundCancelledTicketsOffersDiscounts(data: {
  booking: IProductSlotBooking;
  applicationUuid: string | undefined;
  cancelledTickets: {
    cancelledDiscountItemsCount: number;
    priceId: number;
    categoryId: number;
    productId: number;
    offerId: number;
  };
}) {
  console.log(
    "---------- hi from findAndRefundCancelledTicketsOffersDiscounts"
  );
  console.log("------- data");
  console.log(cloneDeep(data));
  // TODO HAMZA:
  // Get all cancelled PSS with same priceID mentioned inside data.cancelledTickets
  // Get from them the one discounted by the offrApplication they are in the accounting with the same uuid
  // Exclude from them the ones already refunded.
  // Create one offer refund accounting for the pss to refund

  // 1- Get all cancelled PSS with same priceID mentioned inside data.cancelledTickets
  const cancelledPSS = data.booking.pssList.filter(
    (ps) =>
      ps.bookingStatus === BookingStatus.Canceled &&
      ps.priceId === data.cancelledTickets.priceId
  );
  console.log("------- cancelledPSS");
  console.log(cancelledPSS);

  // 2- Get from them the one discounted by the offerApplication they are in the offer discount accounting
  const cancelledPSSWithOffer: IProductSlotState[] = [];
  const offerDiscountAccounting: IAccounting | undefined =
    data.booking.accountings.find((ac) => ac.uuid === data.applicationUuid);
  const pssUuidsIncludedInOfferDiscountAccounting =
    offerDiscountAccounting?.accountingEntriesList.map(
      (ae) => ae.productSlotStateUuid
    );
  for (let i = 0; i < cancelledPSS.length; i++) {
    const pssUuid = cancelledPSS[i].uuid;
    if (
      pssUuidsIncludedInOfferDiscountAccounting &&
      pssUuidsIncludedInOfferDiscountAccounting.includes(pssUuid)
    ) {
      // So this PSS is discounted by this offer, and we know already it is cancelled, lets add it to the pss list that will be refunded
      cancelledPSSWithOffer.push(cancelledPSS[i]);
    }
  }
  console.log("------- cancelledPSSWithOffer");
  console.log(cancelledPSSWithOffer);

  // 3- Exclude from them the ones already refunded, we identify them by not finding them in any of this offerApplication refund accountings
  const relatedOfferRefundAccountings: IAccounting[] =
    data.booking.accountings.filter(
      (ac) =>
        ac.refund &&
        ac.originalAccountingUuid === data.applicationUuid &&
        ac.accountingEntriesList.some(
          (ae) => ae.offerId === data.cancelledTickets.offerId
        )
    );
  console.log("------- relatedOfferRefundAccountings");
  console.log(relatedOfferRefundAccountings);
  const alreadyRefundedPssUuids = relatedOfferRefundAccountings
    .map((acc) => acc.accountingEntriesList)
    .flat()
    .map((ae) => ae.productSlotStateUuid);
  console.log("------- alreadyRefundedPssUuids");
  console.log(alreadyRefundedPssUuids);
  // Lets check for each pss of cancelledPSSWithOffer if it is already refunded or not
  const cancelledPSSWithOfferToRefund: IProductSlotState[] = [];
  for (let i = 0; i < cancelledPSSWithOffer.length; i++) {
    const pssUuid = cancelledPSSWithOffer[i].uuid;
    if (alreadyRefundedPssUuids.includes(pssUuid)) {
      // so this pss is already refunded for the same offer
      // lets move to the next one
      continue;
    } else {
      // so this pss is not refunded for this offer, lets consider it in the refund
      cancelledPSSWithOfferToRefund.push(cancelledPSSWithOffer[i]);
    }
  }
  console.log("------- cancelledPSSWithOfferToRefund");
  console.log(cancelledPSSWithOfferToRefund);

  // 4- Create one offer refund accounting for the pss to refund
  const refundAccounting = createCancelPSSDiscountAccounting(
    data.booking,
    cancelledPSSWithOfferToRefund,
    offerDiscountAccounting as IAccounting
  );
  console.log("------- refundAccounting");
  console.log(refundAccounting);
  if (refundAccounting) {
    data.booking.accountings.push(refundAccounting);
  }

  BookingModule.updateBookingAccountings({
    bookingUUID: data.booking.uuid as string,
    accountings: data.booking.accountings,
  });
}

/**
 * Checks if an offer discount has already been refunded, by comparing discount accountings vs refund accountings for each pss in accounting entries
 * it then returns a list of PSS that have non-refunded offer discounts applied
 */
export function getPssWithoutOfferRefunds(
  booking: IProductSlotBooking,
  accounting: IAccounting
) {
  const pssWithoutOfferRefunds = [];
  const pssUuidList = accounting.accountingEntriesList.map(
    (ae) => ae.productSlotStateUuid
  );

  for (let i = 0; i < pssUuidList.length; i++) {
    const pssUuid = pssUuidList[i];
    // get sum of offer discounts applied on the pss
    const offerAccountings = booking.accountings.filter(
      (acc) =>
        (acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_OFFER) &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
        acc.state === 5 &&
        !acc.refund &&
        acc.accountingEntriesList.some(
          (ae) => ae.productSlotStateUuid === pssUuid
        )
    );
    const offerAes = offerAccountings
      .flatMap((acc) => acc.accountingEntriesList)
      .filter((ae) => ae.productSlotStateUuid === pssUuid)
      .map((ae) => ae.value);
    const offerDiscountSum = sumArray(offerAes);

    // get sum of offer discounts refunded for each pss
    const offerRefundAccountings = booking.accountings.filter(
      (acc) =>
        (acc.paymentMethod === PaymentMethods.METHOD_POS_OFFER ||
          acc.paymentMethod === PaymentMethods.METHOD_OFFER) &&
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
        acc.state === 5 &&
        acc.refund &&
        acc.accountingEntriesList.some(
          (ae) => ae.productSlotStateUuid === pssUuid
        )
    );
    const offerRefundAes = offerRefundAccountings
      .flatMap((acc) => acc.accountingEntriesList)
      .filter((ae) => ae.productSlotStateUuid === pssUuid)
      .map((ae) => ae.value);
    const offerRefundSum = sumArray(offerRefundAes) * -1; // as refunds are negative

    // if the sum of discounts does not equal the sum of refunds, then another refund can be created for the pss if we're canceling all offer applications
    if (offerDiscountSum !== offerRefundSum) {
      pssWithoutOfferRefunds.push(pssUuid);
    }
  }

  return pssWithoutOfferRefunds;
}

export function isOfferAccountingRefunded(
  offerAccounting: IAccounting,
  offerRefundAccountings: IAccounting[],
  alreadyProcessedAccountings: (string | null)[] // if this function is called in a loop, alreadyProcessedAccountings should be pased so a single refund acc isn't considered more than one time
) {
  for (const refundAcc of offerRefundAccountings) {
    // Skip if this refund accounting is already processed
    if (alreadyProcessedAccountings.includes(refundAcc.uuid)) {
      continue;
    }
    if (
      offerAccounting.promocode &&
      refundAcc.promocode !== offerAccounting.promocode
    ) {
      continue;
    }
    // Check for matching entries in the accountingEntriesList
    for (const refundEntry of refundAcc.accountingEntriesList) {
      for (const accountingEntry of offerAccounting.accountingEntriesList) {
        if (
          refundEntry.productSlotStateUuid ===
            accountingEntry.productSlotStateUuid &&
          refundEntry.value * -1 === accountingEntry.value
        ) {
          return refundAcc;
        }
      }
    }
  }
}
